// Per-viewer display preferences (browser storage is a convenience here; defaults work without it).
import { signal } from "@preact/signals";

const SKY_KEY = "degen-world:sky";

/** "real": sky and clock follow your device's local time (default). "game": the fast in-game day. */
export const skyMode = signal<"real" | "game">(read());

function read(): "real" | "game" {
  try {
    return localStorage.getItem(SKY_KEY) === "game" ? "game" : "real";
  } catch {
    return "real";
  }
}

export function setSkyMode(m: "real" | "game") {
  skyMode.value = m;
  try {
    localStorage.setItem(SKY_KEY, m);
  } catch {
    /* storage blocked: still works for this session */
  }
}

/** Minutes since local midnight on this device. */
export function localMinute(d = new Date()): number {
  return d.getHours() * 60 + d.getMinutes();
}

export function localClock(d = new Date()): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** The clock the HUD and phone show: your local time, or the game clock. */
export function displayClock(gameClock: string): string {
  return skyMode.value === "real" ? localClock() : gameClock;
}

// ---------------------------------------------------------------- accessibility + audio (§8)
export interface UiPrefs {
  colorblind: boolean; // blue/orange instead of green/red for up/down
  textScale: number; // 0.85 - 1.35
  reducedMotion: boolean;
  music: boolean; // district music (off by default on first launch)
  volume: number; // master 0..1
}

const PREFS_KEY = "degen-world:ui-prefs";
const DEFAULTS: UiPrefs = {
  colorblind: false,
  textScale: 1,
  reducedMotion: typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches,
  music: false,
  volume: 0.7,
};

function readPrefs(): UiPrefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

export const uiPrefs = signal<UiPrefs>(readPrefs());

export function setUiPrefs(patch: Partial<UiPrefs>) {
  uiPrefs.value = { ...uiPrefs.value, ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(uiPrefs.value));
  } catch {
    /* ignore */
  }
  applyUiPrefs();
}

/** Classes + CSS vars on <html>: colorblind palette, text size, reduced motion. */
export function applyUiPrefs() {
  const p = uiPrefs.value;
  const el = document.documentElement;
  el.classList.toggle("cb-safe", p.colorblind);
  el.classList.toggle("reduce-motion", p.reducedMotion);
  el.style.setProperty("--text-scale", String(p.textScale));
}
